import { Button } from '@components/v2/button'
import {
  Input,
  InputCell,
  InputCnpj,
  InputEmail,
  Select
} from '@components/form'
import { FreeMarketLeadsApi } from '@services/api/free-market-leads'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import { FreeMarketLeadsSpecification } from './schema-form'
import {
  MultipleFileUploadContainer,
  UploadableFile
} from './multiple-file-upload-container'
import { FreeMarketLeadFieldsOptions } from './selects-options'
import { useToasts } from 'react-toast-notifications'
import { event } from '@utils/gtag'
import { SuccessModal } from '@components/v2/successModal'

interface FormData {
  name: string
  email: string
  phone: string
  document: string
  consumerUnits: {
    value: string
    text: string
  }
  energyBillImages: UploadableFile[]
}

export const FreeMarketLeadForm = () => {
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [resetList, setResetList] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const { addToast } = useToasts()
  const initialValues = {
    name: '',
    email: '',
    phone: '',
    document: '',
    consumerUnits: '',
    energyBillImages: []
  }

  const preparePayload = (formValues: FormData) => {
    const urls = formValues.energyBillImages.map(
      (image: UploadableFile) => image.url
    )

    const payload = {
      name: formValues.name,
      email: formValues.email,
      phone: formValues.phone,
      document: formValues.document.replace(/\D/g, ''),
      consumer_units: formValues.consumerUnits.value,
      origin_url: top.window.document.location.href,
      energy_bill_images: urls
    }

    return payload
  }

  const onCreate = async (formValues: any, { resetForm }: any) => {
    try {
      setLoading(true)
      await FreeMarketLeadsApi.create(preparePayload(formValues))
      event({
        action: 'Botão Enviar Lead Mercado Livre',
        category: 'Botão Enviar Lead Mercado Livre',
        label: 'Botão Enviar Lead Mercado Livre',
        value: 0
      })
      setShowModal(true)
      resetForm({})
      setResetList(true)
    } catch (error) {
      console.log({ error })
      addToast('Ops, algo deu errado', {
        autoDismiss: true,
        appearance: 'error'
      })
    } finally {
      setLoading(false)
      setResetList(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <Formik
        initialValues={initialValues}
        onSubmit={onCreate}
        validationSchema={FreeMarketLeadsSpecification.schema}
      >
        {({ isValid, dirty, handleSubmit, values }) => (
          <Form>
            <div className="flex flex-col">
              <Input
                placeholder="Nome Completo"
                name="name"
                id="name"
                className="input pl-6 font-extralight"
                tabIndex={1}
              />
              <InputEmail
                placeholder="E-mail"
                name="email"
                id="email"
                className="input text-lg pl-6 font-extralight"
                tabIndex={2}
              />
              <InputCell
                placeholder="Telefone"
                name="phone"
                id="phone"
                className="input text-lg pl-6 font-extralight"
                tabIndex={3}
              />

              <InputCnpj
                placeholder="CNPJ"
                name="document"
                className="input text-lg pl-6 font-extralight"
                tabIndex={4}
              />

              <Select
                id="consumerUnits"
                name="consumerUnits"
                options={FreeMarketLeadFieldsOptions.consumerUnits}
                itemSelected={values?.consumerUnits?.value}
                placeholder="Unidade Consumidora"
                labelExtractor={({ text }) => text}
                tabIndex={5}
              />
            </div>

            <div className="flex flex-col my-12 items-center">
              <MultipleFileUploadContainer
                name="energyBillImages"
                resetList={resetList}
                uploadingState={setUploading}
              />
            </div>

            <div className="mx-auto flex mb-4">
              <Button
                type="button"
                color="primary"
                className="rounded-4xl w-full"
                disabled={!(isValid && dirty) || uploading || loading}
                loading={loading}
                onClick={() => handleSubmit()}
                tabIndex={6}
              >
                Enviar
              </Button>
            </div>
            <small className="block text-center text-sm text-v2-sonic-silver sm:text-base lg:text-lg">
              Ao continuar você está de acordo com os termos e condições de uso
              do Portal Solar
            </small>
          </Form>
        )}
      </Formik>
      <SuccessModal setShowModal={setShowModal} showModal={showModal} />
    </div>
  )
}
